import { Language, translate, SUPPORTED_LANGUAGES } from "../i18n/translations";

/**
 * Advanced translation utilities for complex scenarios
 */

/**
 * Translate with string interpolation
 * @example
 * translateWithVars("messages.welcome", "en", { name: "John" })
 * // JSON: "messages.welcome": "Welcome, {{name}}!"
 * // Result: "Welcome, John!"
 */
export const translateWithVars = (
  key: string,
  language: Language,
  variables: Record<string, any>
): string => {
  let text = translate(key, language);

  Object.entries(variables).forEach(([varName, value]) => {
    text = text.replace(`{{${varName}}}`, String(value));
  });

  return text;
};

/**
 * Get plural form of translation
 * @example
 * translatePlural("items.count", "en", 5)
 * // JSON: "items.count_singular": "1 item", "items.count_plural": "{{count}} items"
 * // Result: "5 items"
 */
export const translatePlural = (
  keyPrefix: string,
  language: Language,
  count: number
): string => {
  const key =
    count === 1
      ? `${keyPrefix}_singular`
      : `${keyPrefix}_plural`;
  return translateWithVars(key, language, { count });
};

/**
 * Get all available translations in a feature
 * @example
 * getAllFeatureTranslations("dashboard", "en")
 * // Returns all dashboard.* translations
 */
export const getAllFeatureTranslations = (
  feature: string,
  language: Language
): Record<string, string> => {
  const result: Record<string, string> = {};
  // This requires building the translations object structure
  // Implementation depends on your translation file structure
  return result;
};

/**
 * Validate translation keys exist across all languages
 * Useful during development to catch missing translations
 */
export const validateTranslations = (
  keys: string[]
): { valid: boolean; missing: { key: string; languages: Language[] }[] } => {
  const missing: { key: string; languages: Language[] }[] = [];

  keys.forEach((key) => {
    const missingLangs: Language[] = [];

    SUPPORTED_LANGUAGES.forEach((lang) => {
      const value = translate(key, lang);
      // If translation returns the key itself, it means it wasn't found
      if (value === key) {
        missingLangs.push(lang);
      }
    });

    if (missingLangs.length > 0) {
      missing.push({ key, languages: missingLangs });
    }
  });

  return {
    valid: missing.length === 0,
    missing,
  };
};

/**
 * Batch validate translations
 */
export const validateAllTranslations = (
  translationObject: any,
  prefix = ""
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const checkKeys = (obj: any, currentPrefix: string) => {
    Object.keys(obj).forEach((key) => {
      const fullKey = currentPrefix ? `${currentPrefix}.${key}` : key;

      if (typeof obj[key] === "object" && obj[key] !== null) {
        checkKeys(obj[key], fullKey);
      } else if (typeof obj[key] === "string") {
        const validation = validateTranslations([fullKey]);
        if (!validation.valid) {
          validation.missing.forEach((item) => {
            errors.push(
              `Missing translation for "${fullKey}" in: ${item.languages.join(", ")}`
            );
          });
        }
      }
    });
  };

  checkKeys(translationObject, prefix);

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Get language-specific formatting options
 */
export const getLanguageFormatting = (language: Language): Intl.NumberFormat => {
  const localeMap: Record<Language, string> = {
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    zh: "zh-CN",
    ar: "ar-SA",
    pt: "pt-PT",
    hi: "hi-IN",
    sw: "sw-KE",
    yo: "yo-NG",
    ig: "ig-NG",
    ha: "ha-NG"
  };

  return new Intl.NumberFormat(localeMap[language]);
};

/**
 * Format currency based on language
 */
export const formatCurrency = (
  amount: number,
  language: Language,
  currency = "USD"
): string => {
  const localeMap: Record<Language, string> = {
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    zh: "zh-CN",
    ar: "ar-SA",
    pt: "pt-PT",
    hi: "hi-IN",
    sw: "sw-KE",
    yo: "yo-NG",
    ig: "ig-NG",
    ha: "ha-NG"
  };

  return new Intl.NumberFormat(localeMap[language], {
    style: "currency",
    currency: currency,
  }).format(amount);
};

/**
 * Format date based on language
 */
export const formatDate = (
  date: Date,
  language: Language,
  options?: Intl.DateTimeFormatOptions
): string => {
  const localeMap: Record<Language, string> = {
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    zh: "zh-CN",
    ar: "ar-SA",
    pt: "pt-PT",
    hi: "hi-IN",
    sw: "sw-KE",
    yo: "yo-NG",
    ig: "ig-NG",
    ha: "ha-NG"
  };

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat(localeMap[language], defaultOptions).format(
    date
  );
};

/**
 * Format time based on language
 */
export const formatTime = (
  date: Date,
  language: Language,
  options?: Intl.DateTimeFormatOptions
): string => {
  const localeMap: Record<Language, string> = {
    en: "en-US",
    es: "es-ES",
    fr: "fr-FR",
    zh: "zh-CN",
    ar: "ar-SA",
    pt: "pt-PT",
    hi: "hi-IN",
    sw: "sw-KE",
    yo: "yo-NG",
    ig: "ig-NG",
    ha: "ha-NG"
  };

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    ...options,
  };

  return new Intl.DateTimeFormat(localeMap[language], defaultOptions).format(
    date
  );
};

/**
 * Get appropriate text direction for language
 */
export const getTextDirection = (language: Language): "ltr" | "rtl" => {
  return language === "ar" ? "rtl" : "ltr";
};

/**
 * Get language name in the language itself (endonym)
 */
export const getLanguageEndonym = (language: Language): string => {
  const endonyms: Record<Language, string> = {
    en: "English",
    es: "Español",
    fr: "Français",
    zh: "中文",
    ar: "العربية",
    pt: "Português",
    hi: "हिन्दी",
    sw: "Kiswahili",
    yo: "Yorùbá",
    ig: "Igbo",
    ha: "Hausa"
  };

  return endonyms[language];
};

/**
 * Get language name in English (exonym)
 */
export const getLanguageExonym = (language: Language): string => {
  const exonyms: Record<Language, string> = {
    en: "English",
    es: "Spanish",
    fr: "French",
    zh: "Chinese",
    ar: "Arabic",
    pt: "Portuguese",
    hi: "Hindi",
    sw: "Swahili",
    yo: "Yorùbá",
    ig: "Igbo",
    ha: "Hausa"
  };

  return exonyms[language];
};

/**
 * Check if language is RTL
 */
export const isLanguageRTL = (language: Language): boolean => {
  return language === "ar";
};

/**
 * Get ISO 639-1 language code
 */
export const getLanguageCode = (language: Language): string => {
  const codes: Record<Language, string> = {
    en: "en",
    es: "es",
    fr: "fr",
    zh: "zh",
    ar: "ar",
    pt: "pt",
    hi: "hi",
    sw: "sw",
    yo: "yo",
    ig: "ig",
    ha: "ha"
  };

  return codes[language];
};

/**
 * Create a translation cache for performance
 * Useful for frequently accessed translations
 */
export class TranslationCache {
  private cache: Map<string, string> = new Map();

  set(key: string, value: string): void {
    this.cache.set(key, value);
  }

  get(key: string): string | undefined {
    return this.cache.get(key);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  getSize(): number {
    return this.cache.size;
  }
}

/**
 * Singleton translation cache instance
 */
export const translationCache = new TranslationCache();
